// Create / edit form for a customer, shown in a (wide) modal. Used by CustomerList.
//
// Sections: basics, forecast inputs, contact, notes, and per-hub allocations.
// All numeric/email fields are validated before any API call; codes are unique
// per account (client check + DB constraint).

import { useEffect, useMemo, useState } from "react";
import { Button, Field, Input, Modal, Select, Textarea } from "../../components/ui";
import type {
  Customer,
  CustomerAllocation,
  CustomerInput,
  CustomerLane,
  Hub,
  LhScheduleDay,
  LoadType,
} from "../../lib/types";
import { isUniqueViolation, getErrorMessage } from "../../lib/errors";
import { parseOptionalNumber, isValidEmail } from "../../lib/validation";
import { createCustomer, updateCustomer } from "./customersApi";
import { listCustomerLanes, saveCustomerLanes } from "./lanesApi";
import AllocationsEditor, { type AllocationDraft } from "./AllocationsEditor";
import { allocationIntervalTotals } from "./allocationRules";
import LhWeekEditor, { DAY_NAMES, emptyWeek } from "../lhschedule/LhWeekEditor";
import {
  buildWeekDays,
  listCustomerLhSchedule,
  saveLhSchedule,
  type WeekDayDraft,
} from "../lhschedule/lhScheduleApi";

// Editable per-lane packaging (raw strings; validated on save).
interface LaneDraft {
  loadType: string;
  loadCapacity: string;
  mixCartons: string;
  mixPolybags: string;
  mixIrregular: string;
}
const emptyLane = (): LaneDraft => ({
  loadType: "mix",
  loadCapacity: "",
  mixCartons: "",
  mixPolybags: "",
  mixIrregular: "",
});

// Day-of-week shape presets (ids match the forecast engine). "custom" means the
// factors were calibrated — that now happens per hub on the Calibration page.
const DOW_PROFILE_OPTIONS = [
  { id: "standard", label: "Standard (Network)" },
  { id: "weekday", label: "Weekday Only (Mon–Fri)" },
  { id: "heavy_early", label: "Heavy Mon–Wed" },
  { id: "tue_thu", label: "Tue–Thu Focus" },
  { id: "custom", label: "Custom (calibrated)" },
];

const TIER_OPTIONS = [
  { value: 0, label: "Direct" },
  { value: 1, label: "Head-to-head lane" },
];

const LOAD_TYPE_OPTIONS: { id: LoadType; label: string }[] = [
  { id: "fluid", label: "Fluid load" },
  { id: "pallets", label: "Pallets" },
  { id: "mix", label: "Mix" },
];

interface Props {
  accountId: string;
  customer: Customer | null; // null = creating
  hubs: Hub[];
  existingCustomers: Customer[]; // for the duplicate-code check
  onClose: () => void;
  onSaved: () => void;
}

function toDrafts(allocations: CustomerAllocation[]): AllocationDraft[] {
  return allocations.map((a) => ({
    hubId: a.hubId,
    percentage: String(a.percentage),
    effectiveDate: a.effectiveDate ?? "",
    endDate: a.endDate ?? "",
  }));
}

export default function CustomerForm({
  accountId,
  customer,
  hubs,
  existingCustomers,
  onClose,
  onSaved,
}: Props) {
  const [name, setName] = useState(customer?.name ?? "");
  const [code, setCode] = useState(customer?.code ?? "");
  const [tier, setTier] = useState(customer?.tier ?? 0);
  const [active, setActive] = useState(customer?.active ?? true);
  const [dailyBase, setDailyBase] = useState(customer ? String(customer.dailyBase) : "0");
  const [growth, setGrowth] = useState(customer ? String(customer.growth) : "0");
  const [onboardingDate, setOnboardingDate] = useState(customer?.onboardingDate ?? "");
  const [dowProfile, setDowProfile] = useState(customer?.dowProfile ?? "standard");
  // Preserved on save so an existing calibrated customer isn't wiped; the customer
  // form no longer edits DOW factors (calibration is per hub on the Calibration page).
  const [customDowFactors] = useState<number[] | null>(customer?.customDowFactors ?? null);
  const [contactName, setContactName] = useState(customer?.contactName ?? "");
  const [email, setEmail] = useState(customer?.email ?? "");
  const [phone, setPhone] = useState(customer?.phone ?? "");
  const [notes, setNotes] = useState(customer?.notes ?? "");
  const [allocations, setAllocations] = useState<AllocationDraft[]>(
    customer ? toDrafts(customer.allocations) : []
  );
  // Per-hub-lane state (keyed by hubId): packaging + LH weekly schedule.
  const [laneByHub, setLaneByHub] = useState<Record<string, LaneDraft>>({});
  const [lhByHub, setLhByHub] = useState<Record<string, WeekDayDraft[]>>({});
  const [existingLhHubs, setExistingLhHubs] = useState<string[]>([]);
  const [activeLaneHub, setActiveLaneHub] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing per-lane settings (packaging + LH) when editing a customer.
  useEffect(() => {
    if (!customer) return;
    Promise.all([
      listCustomerLhSchedule(accountId, customer.id),
      listCustomerLanes(accountId, customer.id),
    ])
      .then(([lh, lanes]) => {
        const lhDrafts: Record<string, WeekDayDraft[]> = {};
        for (const l of lh) {
          if (!lhDrafts[l.hubId]) lhDrafts[l.hubId] = emptyWeek();
          lhDrafts[l.hubId][l.day.dayOfWeek] = {
            loads: String(l.day.loads),
            trailerType: l.day.trailerType ?? "",
            arrivalTimes: l.day.arrivalTimes.join(", "),
          };
        }
        setLhByHub(lhDrafts);
        setExistingLhHubs(Object.keys(lhDrafts));

        const laneDrafts: Record<string, LaneDraft> = {};
        for (const ln of lanes) {
          laneDrafts[ln.hubId] = {
            loadType: ln.loadType,
            loadCapacity: ln.loadCapacity != null ? String(ln.loadCapacity) : "",
            mixCartons: String(ln.packageMix.cartons),
            mixPolybags: String(ln.packageMix.polybags),
            mixIrregular: String(ln.packageMix.irregular),
          };
        }
        setLaneByHub(laneDrafts);
      })
      .catch(() => {});
  }, [customer, accountId]);

  // Unique hubs from the current allocations = the lanes that can have a schedule.
  const laneHubIds = useMemo(
    () => [...new Set(allocations.map((a) => a.hubId).filter(Boolean))],
    [allocations]
  );
  const hubName = (id: string) => hubs.find((h) => h.id === id)?.name ?? "Hub";
  // The lane whose weekly grid is currently shown (falls back to the first lane).
  const activeHub = laneHubIds.includes(activeLaneHub) ? activeLaneHub : laneHubIds[0] ?? "";
  const getWeek = (hubId: string) => lhByHub[hubId] ?? emptyWeek();
  const setWeek = (hubId: string, next: WeekDayDraft[]) =>
    setLhByHub((prev) => ({ ...prev, [hubId]: next }));
  const getLane = (hubId: string) => laneByHub[hubId] ?? emptyLane();
  const setLane = (hubId: string, patch: Partial<LaneDraft>) =>
    setLaneByHub((prev) => ({ ...prev, [hubId]: { ...(prev[hubId] ?? emptyLane()), ...patch } }));

  /** Validate + convert the per-lane packaging drafts. Null on error (sets message). */
  function buildLanes(): CustomerLane[] | null {
    const out: CustomerLane[] = [];
    for (const hubId of laneHubIds) {
      const d = getLane(hubId);
      const cap = parseOptionalNumber(d.loadCapacity);
      if (!cap.ok || (cap.value !== null && cap.value < 0)) {
        setError(`${hubName(hubId)} — load capacity must be a positive number.`);
        return null;
      }
      const mcap = parseOptionalNumber(d.mixCartons);
      const mp = parseOptionalNumber(d.mixPolybags);
      const mi = parseOptionalNumber(d.mixIrregular);
      if (!mcap.ok || !mp.ok || !mi.ok) {
        setError(`${hubName(hubId)} — package mix percentages must be numbers.`);
        return null;
      }
      const mix = {
        cartons: Math.round(mcap.value ?? 0),
        polybags: Math.round(mp.value ?? 0),
        irregular: Math.round(mi.value ?? 0),
      };
      if (mix.cartons < 0 || mix.polybags < 0 || mix.irregular < 0) {
        setError(`${hubName(hubId)} — package mix percentages can't be negative.`);
        return null;
      }
      const total = mix.cartons + mix.polybags + mix.irregular;
      if (total !== 0 && total !== 100) {
        setError(`${hubName(hubId)} — package mix must total 100% (or leave all at 0).`);
        return null;
      }
      out.push({
        hubId,
        loadType: d.loadType as LoadType,
        loadCapacity: cap.value === null ? null : Math.round(cap.value),
        packageMix: mix,
      });
    }
    return out;
  }

  function codeIsDuplicate(value: string): boolean {
    const c = value.trim().toLowerCase();
    if (!c) return false;
    return existingCustomers.some(
      (x) => x.id !== customer?.id && (x.code ?? "").trim().toLowerCase() === c
    );
  }

  /** Validate + convert the allocation drafts. Returns null on error (sets message). */
  function buildAllocations(): CustomerAllocation[] | null {
    const rows = allocations.filter((a) => a.hubId);
    const out: CustomerAllocation[] = [];
    for (const row of rows) {
      const pct = parseOptionalNumber(row.percentage);
      if (!pct.ok || pct.value === null || pct.value < 0 || pct.value > 100) {
        setError("Each allocation percentage must be a number between 0 and 100.");
        return null;
      }
      if (row.effectiveDate && row.endDate && row.endDate <= row.effectiveDate) {
        setError("An allocation's 'until' date must be after its effective date.");
        return null;
      }
      out.push({
        hubId: row.hubId,
        percentage: pct.value,
        effectiveDate: row.effectiveDate || undefined,
        endDate: row.endDate || undefined,
      });
    }
    // Date-aware: on every active date the allocations must total exactly 100%.
    const totals = allocationIntervalTotals(out);
    const bad = totals.find((t) => t !== 100);
    if (bad !== undefined) {
      setError(`Allocations must total 100% on every active date — found ${bad}% on some dates.`);
      return null;
    }
    return out;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (codeIsDuplicate(code)) {
      setError(`Code "${code.trim()}" is already used by another customer.`);
      return;
    }

    const baseP = parseOptionalNumber(dailyBase);
    if (!baseP.ok || (baseP.value !== null && baseP.value < 0)) {
      setError("Daily base must be a positive number.");
      return;
    }
    const growthP = parseOptionalNumber(growth);
    if (!growthP.ok || (growthP.value !== null && growthP.value < -100)) {
      setError("Growth must be a number (percent per year).");
      return;
    }
    if (email.trim() && !isValidEmail(email)) {
      setError("Please enter a valid email.");
      return;
    }

    const builtAllocations = buildAllocations();
    if (builtAllocations === null) return;

    const builtLanes = buildLanes();
    if (builtLanes === null) return;

    // Validate each allocated lane's LH schedule.
    const lhPerHub: Record<string, LhScheduleDay[]> = {};
    for (const hubId of laneHubIds) {
      const res = buildWeekDays(getWeek(hubId));
      if (!res.ok) {
        setError(`LH schedule — ${hubName(hubId)} / ${DAY_NAMES[res.dayIndex]}: ${res.message}`);
        return;
      }
      lhPerHub[hubId] = res.days;
    }

    setBusy(true);
    setError(null);
    const input: CustomerInput = {
      name,
      code,
      tier,
      active,
      dailyBase: baseP.value === null ? 0 : Math.round(baseP.value),
      growth: growthP.value ?? 0,
      onboardingDate,
      dowProfile,
      customDowFactors: dowProfile === "custom" ? customDowFactors : null,
      calibrationWeeks: customer?.calibrationWeeks ?? 3,
      lastCalibratedAt: customer?.lastCalibratedAt ?? null,
      contactName,
      email,
      phone,
      notes,
      allocations: builtAllocations,
    };
    try {
      const saved = customer
        ? await updateCustomer(accountId, customer.id, input)
        : await createCustomer(accountId, input);
      // Save per-lane packaging (wholesale replace handles removed hubs).
      await saveCustomerLanes(accountId, saved.id, builtLanes);
      // Save each allocated lane's LH schedule; clear lanes no longer allocated.
      for (const hubId of laneHubIds) {
        await saveLhSchedule(accountId, saved.id, hubId, lhPerHub[hubId]);
      }
      for (const hubId of existingLhHubs) {
        if (!laneHubIds.includes(hubId)) {
          await saveLhSchedule(accountId, saved.id, hubId, []);
        }
      }
      onSaved();
    } catch (err: unknown) {
      if (isUniqueViolation(err)) {
        setError(`Code "${code.trim()}" is already used by another customer.`);
      } else {
        setError(getErrorMessage(err, "Failed to save customer."));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={customer ? "Edit customer" : "New customer"} onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-5">
        {/* Basics */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Co" autoFocus />
          </Field>
          <Field label="Code">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ACME" />
          </Field>
          <Field label="Tier">
            <Select value={String(tier)} onChange={(e) => setTier(Number(e.target.value))}>
              {TIER_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>
        </div>

        {/* Forecast inputs */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Forecast inputs
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Daily base (pkg/day)">
              <Input
                type="number"
                min={0}
                value={dailyBase}
                onChange={(e) => setDailyBase(e.target.value)}
                placeholder="1000"
              />
            </Field>
            <Field label="Annual growth (%)">
              <Input
                type="number"
                step="any"
                value={growth}
                onChange={(e) => setGrowth(e.target.value)}
                placeholder="5"
              />
            </Field>
            <Field label="Onboarding date">
              <Input
                type="date"
                value={onboardingDate}
                onChange={(e) => setOnboardingDate(e.target.value)}
              />
            </Field>
            <Field label="Day-of-week profile">
              <Select value={dowProfile} onChange={(e) => setDowProfile(e.target.value)}>
                {DOW_PROFILE_OPTIONS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Day-of-week factors are learned per hub on the{" "}
            <span className="font-medium text-slate-600">Calibration</span> page (a customer can ship
            a different DOW shape to each hub).
            {dowProfile === "custom" && " This customer currently uses calibrated factors."}
          </p>
        </div>

        {/* Contact */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Contact</h3>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Contact name">
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </Field>
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Phone">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
          </div>
        </div>

        {/* Allocations */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Hub allocations
          </h3>
          <AllocationsEditor hubs={hubs} value={allocations} onChange={setAllocations} />
        </div>

        {/* LH schedule per allocated hub lane */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Hub lane settings (per hub)
          </h3>
          {laneHubIds.length === 0 ? (
            <p className="text-sm text-slate-500">
              Add a hub allocation above to configure its load, packaging, and schedule.
            </p>
          ) : (
            <div className="rounded-lg border border-slate-200">
              {/* One tab per allocated hub lane — keeps the form short. */}
              <div className="flex flex-wrap gap-1 border-b border-slate-200 p-2">
                {laneHubIds.map((hubId) => (
                  <button
                    key={hubId}
                    type="button"
                    onClick={() => setActiveLaneHub(hubId)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                      hubId === activeHub
                        ? "bg-brand-50 text-brand-700"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {hubName(hubId)}
                  </button>
                ))}
              </div>
              {activeHub && (
                <div className="space-y-4 p-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Load type">
                      <Select
                        value={getLane(activeHub).loadType}
                        onChange={(e) => setLane(activeHub, { loadType: e.target.value })}
                      >
                        {LOAD_TYPE_OPTIONS.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Load capacity (pkg/load)">
                      <Input
                        type="number"
                        min={0}
                        value={getLane(activeHub).loadCapacity}
                        onChange={(e) => setLane(activeHub, { loadCapacity: e.target.value })}
                        placeholder="1500"
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Cartons %">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={getLane(activeHub).mixCartons}
                        onChange={(e) => setLane(activeHub, { mixCartons: e.target.value })}
                      />
                    </Field>
                    <Field label="Polybags %">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={getLane(activeHub).mixPolybags}
                        onChange={(e) => setLane(activeHub, { mixPolybags: e.target.value })}
                      />
                    </Field>
                    <Field label="Irregular %">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={getLane(activeHub).mixIrregular}
                        onChange={(e) => setLane(activeHub, { mixIrregular: e.target.value })}
                      />
                    </Field>
                  </div>
                  <p className="text-xs text-slate-500">
                    Mix total:{" "}
                    {(Number(getLane(activeHub).mixCartons) || 0) +
                      (Number(getLane(activeHub).mixPolybags) || 0) +
                      (Number(getLane(activeHub).mixIrregular) || 0)}
                    % (must be 0 or 100)
                  </p>
                  <div className="border-t border-slate-200 pt-3">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Weekly schedule
                    </div>
                    <LhWeekEditor value={getWeek(activeHub)} onChange={(n) => setWeek(activeHub, n)} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notes */}
        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : customer ? "Save changes" : "Create customer"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
